import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, requestYieldCallback } from '@theme/utilities';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent, SlideshowSelectEvent } from '@theme/events';
import { morph } from '@theme/morph';

/** @type {Promise<unknown> | null} */
let cardSwatchesModulePromise = null;

/**
 * Card swatches live in a separate module so listing pages do not pull the PDP picker
 * (and paint-calculator-core) via a static import. Load once when a card actually has markup.
 * @param {ParentNode} root
 */
function loadCardSwatchesIfNeeded(root) {
  if (!root.querySelector('swatches-variant-picker-component')) return;
  cardSwatchesModulePromise ??= import('@theme/product-card-swatches');
}

/** @type {Promise<unknown> | null} */
let staalkiezerModulePromise = null;
/** @type {Promise<unknown> | null} */
let staalFormaatModulePromise = null;
/** @type {Promise<unknown> | null} */
let quickAddModulePromise = null;
/** @type {Promise<unknown> | null} */
let productFormModulePromise = null;

/**
 * SHO-10262: kaarten die via Section Rendering / Algolia binnenkomen hebben vaak geen
 * extra script-tag. product-card.js is critical shell; importeer companions op markup.
 * @param {ParentNode} root
 */
function loadCardCompanionModules(root) {
  if (root.querySelector('[data-staal-knop]')) {
    staalkiezerModulePromise ??= import('@theme/staalkiezer');
  }
  if (root.querySelector('[data-staal-varianten]')) {
    staalFormaatModulePromise ??= import('@theme/staal-formaatkiezer');
  }
  if (root.querySelector('quick-add-component')) {
    quickAddModulePromise ??= import('@theme/quick-add');
  }
  if (root.querySelector('product-form-component')) {
    productFormModulePromise ??= import('@theme/product-form');
  }
}

/**
 * A card preloads its next slide so paging to it does not flash white. That only pays off once
 * the card is somewhere the visitor can reach it — doing it for every card on connect fetched 87
 * full-size images nobody sees on a 120-card page (SHO-9664). One observer serves the whole page;
 * a card drops out as soon as it has fired.
 */
const PREVIEW_PRELOAD_MARGIN = '200px';

/** @type {IntersectionObserver | null} */
let previewPreloadObserver = null;

/** @type {WeakMap<Element, () => void>} */
const previewPreloadCallbacks = new WeakMap();

/**
 * @param {Element} card
 * @param {() => void} preload
 */
function observeForPreviewPreload(card, preload) {
  previewPreloadObserver ??= new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        previewPreloadObserver?.unobserve(entry.target);
        previewPreloadCallbacks.get(entry.target)?.();
        previewPreloadCallbacks.delete(entry.target);
      }
    },
    { rootMargin: PREVIEW_PRELOAD_MARGIN }
  );

  previewPreloadCallbacks.set(card, preload);
  previewPreloadObserver.observe(card);
}

/** @param {Element} card */
function unobserveForPreviewPreload(card) {
  previewPreloadObserver?.unobserve(card);
  previewPreloadCallbacks.delete(card);
}

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 * @property {HTMLImageElement} [variantPreview] - Preview layer painted from a swatch, over the gallery.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
  requiredRefs = ['productCardLink'];

  get productPageUrl() {
    return this.refs.productCardLink.href;
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null if none selected
   */
  getSelectedVariantId() {
    const checkedInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('input[type="radio"]:checked[data-variant-id]')
    );

    return checkedInput?.dataset.variantId || null;
  }

  /**
   * Gets the product card link element
   * @returns {HTMLAnchorElement | null} The product card link or null
   */
  getProductCardLink() {
    return this.refs.productCardLink || null;
  }

  /**
   * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
   * @param {Event} event - The event that triggered the navigation.
   * @param {URL} url - The URL to navigate to.
   */
  #navigateToURL = (event, url) => {
    // Check for modifier keys that should open in new tab/window (only for mouse events)
    const shouldOpenInNewTab =
      event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

    if (shouldOpenInNewTab) {
      event.preventDefault();
      window.open(url.href, '_blank');
      return;
    } else {
      window.location.href = url.href;
    }
  };

  connectedCallback() {
    super.connectedCallback();

    const link = this.refs.productCardLink;
    if (!(link instanceof HTMLAnchorElement)) throw new Error('Product card link not found');

    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);

    // Preload the next image on the slideshow to avoid white flashes on previewImage, but wait
    // until the card is near enough to the viewport for that image to be reachable.
    observeForPreviewPreload(this, () => {
      if (this.refs.slideshow?.isNested) this.#preloadNextPreviewImage();
    });

    // Slides past the first render without src/srcset (SHO-9774) and stay empty until the
    // visitor reaches for the card. Pointerenter covers mouse, touchstart fires the moment a
    // finger lands (ahead of any swipe), focusin covers keyboard.
    this.addEventListener('pointerenter', this.#hydrateDeferredMedia);
    this.addEventListener('touchstart', this.#hydrateDeferredMedia, { passive: true });
    this.addEventListener('focusin', this.#hydrateDeferredMedia);

    loadCardSwatchesIfNeeded(this);
    loadCardCompanionModules(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    unobserveForPreviewPreload(this);
    this.removeEventListener('pointerenter', this.#hydrateDeferredMedia);
    this.removeEventListener('touchstart', this.#hydrateDeferredMedia);
    this.removeEventListener('focusin', this.#hydrateDeferredMedia);
  }

  /**
   * Moves a deferred image's URLs (SHO-9774) into their real attributes so the browser fetches it.
   * @param {Element | null | undefined} img
   */
  #hydrateImage(img) {
    if (!(img instanceof HTMLImageElement)) return;
    const { srcset, src } = img.dataset;
    if (srcset) {
      img.setAttribute('srcset', srcset);
      img.removeAttribute('data-srcset');
    }
    if (src) {
      img.setAttribute('src', src);
      img.removeAttribute('data-src');
    }
  }

  /**
   * Fetches every deferred slide of this card on the first sign of interest. The listeners
   * stay armed: a filter/sort morph can reuse this element and hand it fresh data-src
   * slides after the first hydration, and the selector is free once nothing is deferred.
   */
  #hydrateDeferredMedia = () => {
    for (const img of this.querySelectorAll('img[data-srcset], img[data-src]')) {
      this.#hydrateImage(img);
    }
  };

  #preloadNextPreviewImage() {
    // Desktop keeps the near-viewport next-slide preload (SHO-9664). On mobile that would
    // still fetch a second image for every card scrolled past, so there the slides wait for
    // the touchstart hydration above (SHO-9774).
    if (!isDesktopBreakpoint()) return;
    const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
    const img = currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]');
    if (!img) return;
    // Drop lazy before the srcset lands: sizes="auto, ..." is only defined for lazy
    // images, so hydrating first could make the browser evaluate the source list twice.
    img.removeAttribute('loading');
    this.#hydrateImage(img);
  }

  /**
   * Handles the variant selected event.
   * @param {VariantSelectedEvent} event - The variant selected event.
   */
  #handleVariantSelected = (event) => {
    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateSelectedOption(event.detail.resource.id);
    }
  };

  /**
   * Handles the variant update event.
   * Updates price, checks for unavailable variants, and updates product URL.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    // Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
    // by this component and should not affect anything outside the card.
    event.stopPropagation();

    this.updatePrice(event);
    this.#isUnavailableVariantSelected(event);
    this.#updateProductUrl(event);
    this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

    if (event.target !== this.variantPicker) {
      this.variantPicker?.updateVariantPicker(event.detail.data.html);
    }

    this.#updateVariantImages();
    this.#previousSlideIndex = null;

    // Remove attribute after re-rendering since a variant selection has been made
    this.removeAttribute('data-no-swatch-selected');

    // Force overflow list to reflow after variant update
    // This fixes an issue where the overflow counter doesn't update properly in some browsers
    this.#updateOverflowList();
  };

  /**
   * Forces the overflow list to recalculate by dispatching a reflow event.
   * This ensures the overflow counter displays correctly after variant updates.
   */
  #updateOverflowList() {
    const overflowList = this.querySelector('swatches-variant-picker-component overflow-list');
    if (!overflowList || overflowList.hasAttribute('static-visible')) return;

    const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
    if (!isActiveOverflowList) return;

    // Use requestAnimationFrame to ensure DOM has been updated
    requestAnimationFrame(() => {
      // Dispatch a reflow event to trigger recalculation
      overflowList.dispatchEvent(
        new CustomEvent('reflow', {
          bubbles: true,
          detail: {},
        })
      );
    });
  }

  /**
   * Updates the DOM with a new price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice(event) {
    const priceContainer = this.querySelectorAll(`product-price [ref='priceContainer']`)[1];
    const newPriceElement = event.detail.data.html.querySelector(`product-price [ref='priceContainer']`);

    if (newPriceElement && priceContainer) {
      morph(priceContainer, newPriceElement);
    }
  }

  /**
   * Updates the product URL based on the variant update event.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #updateProductUrl(event) {
    const anchorElement = event.detail.data.html?.querySelector('product-card a');
    const featuredMediaUrl = event.detail.data.html
      ?.querySelector('product-card-link')
      ?.getAttribute('data-featured-media-url');

    // If the product card is inside a product link, update the product link's featured media URL
    if (featuredMediaUrl && this.closest('product-card-link'))
      this.closest('product-card-link')?.setAttribute('data-featured-media-url', featuredMediaUrl);

    if (anchorElement instanceof HTMLAnchorElement) {
      // If the href is empty, don't update the product URL eg: unavailable variant
      if (anchorElement.getAttribute('href')?.trim() === '') return;

      const productUrl = anchorElement.href;
      const { productCardLink, productTitleLink, cardGalleryLink } = this.refs;

      productCardLink.href = productUrl;
      if (cardGalleryLink instanceof HTMLAnchorElement) {
        cardGalleryLink.href = productUrl;
      }
      if (productTitleLink instanceof HTMLAnchorElement) {
        productTitleLink.href = productUrl;
      }
    }
  }

  /**
   * Checks if an unavailable variant is selected.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #isUnavailableVariantSelected(event) {
    const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
      event.detail.data.html.querySelectorAll('input:checked')
    );

    for (const variant of allVariants) {
      this.#toggleAddToCartButton(variant.dataset.optionAvailable === 'true');
    }
  }

  /**
   * Toggles the add to cart button state.
   * @param {boolean} enable - Whether to enable or disable the button.
   */
  #toggleAddToCartButton(enable) {
    const addToCartButton = this.querySelector('.add-to-cart__button button');

    if (addToCartButton instanceof HTMLButtonElement) {
      addToCartButton.disabled = !enable;
    }
  }

  /**
   * Finds a slide by media id inside a slideshow.
   * @param {Element} slideshow
   * @param {string} id
   * @returns {Element | null}
   */
  #findSlideInSlideshow(slideshow, id) {
    const idStr = String(id);
    const fromRefs = /** @type {{ slides?: Element[] } | undefined} */ (slideshow).refs?.slides?.find(
      (slide) => slide.getAttribute('slide-id') == idStr
    );
    if (fromRefs) return fromRefs;

    return (
      Array.from(slideshow.querySelectorAll('[slide-id]')).find((slide) => slide.getAttribute('slide-id') == idStr) ??
      null
    );
  }

  /**
   * Reads the colour preview a swatch carries. Swatches for media the product owns carry nothing:
   * their slide is already in this gallery, and selecting it costs no bytes and no request.
   * @param {Element | null | undefined} element - The swatch input, or its label.
   * @returns {{ previewSrc: string | null, productTitle: string | null }}
   */
  #swatchPreview(element) {
    const label = /** @type {HTMLElement | null} */ (
      element instanceof Element ? (element.closest('label') ?? element) : null
    );

    return {
      previewSrc: label?.dataset.previewSrc ?? null,
      productTitle: label?.dataset.productTitle ?? null,
    };
  }

  /**
   * Paints a linked colour into the preview layer that sits over the gallery.
   * @param {string} src
   * @returns {boolean} Whether the card has a preview layer to paint into.
   */
  #showVariantPreview(src) {
    const image = this.refs.variantPreview;
    if (!(image instanceof HTMLImageElement)) return false;

    if (image.getAttribute('src') !== src) image.setAttribute('src', src);
    image.hidden = false;

    return true;
  }

  /** Takes the preview layer off again so the gallery shows through. */
  #hideVariantPreview() {
    const image = this.refs.variantPreview;
    if (image instanceof HTMLImageElement) image.hidden = true;
  }

  /**
   * Shows the media belonging to a swatch. Media the product owns is a slide in this gallery, so it gets
   * selected there. Media of a linked colour product is not on this page at all — that is what the preview
   * layer is for, filled from the URL the swatch carries.
   * @param {string | number | undefined | null} id - Media id of the swatch.
   * @param {string | null} [previewSrc] - Image URL of a linked colour.
   * @param {string | null} [productTitle] - Title of the linked colour product.
   * @returns {boolean} Whether anything was shown.
   */
  #showMediaById(id, previewSrc = null, productTitle = null) {
    const { slideshow } = this.refs;
    const mainSlide = id && slideshow ? this.#findSlideInSlideshow(slideshow, String(id)) : null;

    if (mainSlide && slideshow) {
      this.#hideVariantPreview();
      slideshow.select({ id }, undefined, { animate: false });
      this.#setTitle(null);
      return true;
    }

    if (previewSrc && this.#showVariantPreview(previewSrc)) {
      this.#setTitle(productTitle);
      return true;
    }

    return false;
  }

  /**
   * Hides the variant images that are not for the selected variant.
   */
  #updateVariantImages() {
    const { slideshow } = this.refs;
    const selectedOption = this.variantPicker?.selectedOption;
    const selectedImageId = selectedOption?.dataset.optionMediaId;
    const { previewSrc, productTitle } = this.#swatchPreview(selectedOption);

    if (!selectedImageId && !previewSrc) return;

    const mainSlide = selectedImageId && slideshow ? this.#findSlideInSlideshow(slideshow, selectedImageId) : null;

    // Only flip hidden flags when the target exists in the main slideshow. Otherwise every variant slide
    // would become hidden and the card goes blank.
    if (mainSlide && slideshow?.refs?.slides) {
      for (const slide of slideshow.refs.slides) {
        if (slide.getAttribute('variant-image') == null) continue;

        slide.hidden = slide.getAttribute('slide-id') !== selectedImageId;
      }
    }

    this.#showMediaById(selectedImageId, previewSrc, productTitle);
  }

  /**
   * Gets all variant inputs.
   * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
   */
  get allVariants() {
    return this.querySelectorAll('input[data-variant-id]');
  }

  /**
   * Gets the card swatches component.
   * @returns {import('./product-card-swatches').SwatchesVariantPickerComponent | null}
   */
  get variantPicker() {
    return this.querySelector('swatches-variant-picker-component');
  }

  /** @type {string | null} */
  #selectedSwatchMediaId = null;

  /** @type {string | null} */
  #selectedSwatchPreviewSrc = null;

  /** @type {string | null} */
  #selectedSwatchTitle = null;

  /** @type {number | null} */
  #previousSlideIndex = null;

  /**
   * Handles the slideshow select event.
   * @param {SlideshowSelectEvent} event - The slideshow select event.
   */
  #handleSlideshowSelect = (event) => {
    if (event.detail.userInitiated) {
      // Safety net for input that skips the hydration triggers (e.g. assistive tech
      // driving the arrows): make sure no deferred slide is ever shown empty.
      this.#hydrateDeferredMedia();
      this.#previousSlideIndex = event.detail.index;
      // The visitor is browsing the gallery itself, so a colour preview painted on top would
      // swallow every arrow click. Step aside and let the slide they asked for be visible.
      this.#hideVariantPreview();
    }
  };

  /**
   * Previews a variant (main slideshow when possible, else CL carousel).
   * Hover callers pass a PointerEvent — only mouse hovers preview.
   * Selection/tap callers omit the event (or pass a non-pointer event) for immediate feedback.
   * @param {string} id - The id of the variant to preview.
   * @param {Event} [event] - Optional pointer event from hover handlers.
   */
  previewVariant(id, event) {
    // A programmatic call without an event is a deliberate tap/click selection preview.
    // Declarative hover events must identify themselves as mouse events; mobile browsers
    // can emit pointer enter/leave events with an empty pointerType during DOM replacement.
    if (event && event.pointerType !== 'mouse') return;

    this.resetVariant.cancel();

    const swatch =
      event?.target instanceof Element ? event.target : this.querySelector(`label[data-media-id="${id}"]`);
    const { previewSrc, productTitle } = this.#swatchPreview(swatch);

    this.#showMediaById(id, previewSrc, productTitle);
  }

  /**
   * Stores and shows the colour a shopper picked from the swatch row, so leaving the card with the
   * pointer does not undo the choice.
   * @param {string | undefined} mediaId
   * @param {Element | null} [swatch] - The swatch input or label that was picked.
   */
  selectVariant(mediaId, swatch = null) {
    const { previewSrc, productTitle } = this.#swatchPreview(swatch);

    this.#selectedSwatchMediaId = mediaId ?? null;
    this.#selectedSwatchPreviewSrc = previewSrc;
    this.#selectedSwatchTitle = productTitle;
    this.resetVariant.cancel();
    this.#showMediaById(mediaId, previewSrc, productTitle);
  }

  /**
   * Compact PLP grid (zoom-out): no hover image carousel — keep the primary swatch/image.
   * @returns {boolean}
   */
  #isZoomOutGridView() {
    const grid = this.closest('[product-grid-view]');
    return grid?.getAttribute('product-grid-view') === 'zoom-out';
  }

  /**
   * Previews the next image.
   * @param {PointerEvent} event - The pointer event.
   */
  previewImage(event) {
    if (event.pointerType !== 'mouse') return;
    if (this.#isZoomOutGridView()) return;

    const { slideshow } = this.refs;

    if (!slideshow) return;

    this.resetVariant.cancel();

    if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
      slideshow.select(this.#previousSlideIndex, undefined, { animate: false });
    } else {
      slideshow.next(undefined, { animate: false });
      setTimeout(() => this.#preloadNextPreviewImage());
    }
  }

  /**
   * Resets the image to the variant image.
   * @param {PointerEvent} event - The pointer event.
   */
  resetImage(event) {
    if (event.pointerType !== 'mouse') {
      return;
    }
    if (this.#isZoomOutGridView()) return;

    const { slideshow } = this.refs;
    if (!this.variantPicker) {
      if (!slideshow) {
        return;
      }
      slideshow.previous(undefined, { animate: false });
      this.#setTitle(null);
    } else {
      // Restores the picked colour, title included — resetting it here would undo the choice.
      this.#resetVariant();
    }
  }

  /**
   * Resets the image to the selected (or initial) variant.
   * Uses main slideshow when possible, otherwise the CL carousel for that media id.
   */
  #resetVariant = () => {
    const { slideshow } = this.refs;

    if (!slideshow) {
      return;
    }

    const selectedOption = this.variantPicker?.selectedOption;
    const fromSwatch = this.#swatchPreview(selectedOption);
    const id = this.#selectedSwatchMediaId ?? selectedOption?.dataset.optionMediaId;
    const previewSrc = this.#selectedSwatchPreviewSrc ?? fromSwatch.previewSrc;
    const productTitle = this.#selectedSwatchTitle ?? fromSwatch.productTitle;

    if ((id || previewSrc) && this.#showMediaById(id, previewSrc, productTitle)) {
      return;
    }

    // Nothing picked: back to the slide the card opened on.
    this.#hideVariantPreview();
    this.#setTitle(null);

    const initialSlide = slideshow.initialSlide;
    const slideId = initialSlide?.getAttribute('slide-id');

    if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
      slideshow.select({ id: slideId }, undefined, { animate: false });
      return;
    }

    slideshow.previous(undefined, { animate: false });
  };

  /**
   * Intercepts the click event on the product card anchor, we want
   * to use this to add an intermediate state to the history.
   * This intermediate state captures the page we were on so that we
   * navigate back to the same page when the user navigates back.
   * In addition to that, it captures the product card anchor so that we
   * have the specific product card in view.
   *
   * A product card can have other interactive elements like variant picker,
   * so we do not navigate if the click was on one of those elements.
   *
   * @param {Event} event
   */
  navigateToProduct = (event) => {
    if (!(event.target instanceof Element)) return;

    // Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
    if (this.hasAttribute('data-no-navigation')) return;

    const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

    // If the click was on an interactive element, do nothing.
    if (interactiveElement) {
      return;
    }

    const link = this.refs.productCardLink;
    if (!link.href) return;
    const linkURL = new URL(link.href);

    const productCardAnchor = link.getAttribute('id');
    if (!productCardAnchor) return;

    const url = new URL(window.location.href);
    const parent = this.closest('li');
    url.hash = productCardAnchor;
    if (parent && parent.dataset.page) {
      url.searchParams.set('page', parent.dataset.page);
    }

    if (!window.Shopify.designMode) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }

    const targetLink = event.target.closest('a');
    // Let the native navigation handle the click if it was on a link.
    if (!targetLink) {
      this.#navigateToURL(event, linkURL);
    }
  };

  /**
   * Puts a linked colour's product title on the card, or the card's own title back.
   * @param {string | null} title - Title to show, or null for the card's own product title.
   */
  #setTitle(title) {
    const element = this.refs.productTitleLink?.querySelector('p');
    if (!element) return;

    const next = title || this.dataset.productTitle;
    if (next) element.textContent = next;
  }

  /**
   * Resets the variant after hover leave. Mouse-only — touch enter/leave must not undo a tap.
   * @param {Event} [event]
   */
  resetVariant = debounce((event) => {
    // This handler is wired only to pointerleave. Require an explicit mouse pointer so
    // incomplete/synthetic mobile pointer events can never undo the selected swatch.
    if (event?.pointerType !== 'mouse') return;
    this.#resetVariant();
  }, 100);
}

if (!customElements.get('product-card')) {
  customElements.define('product-card', ProductCard);
}
